/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { DateFilterTypeEnum } from "@malmur-technologies/medicalsystem-shared";
import { AccountingMovementInterface, PersonInterface } from "@malmur-technologies/medicalsystem-shared";
import { formatDateForDisplay } from "utils/date.utils";
import { formatPrice } from "utils/number.utils";

/**
 * Properties for the PrintAccountingMovements template component.
 * 
 * @interface PrintAccountingMovementsProps
 * @property {AccountingMovementInterface[]} movements - Array of accounting movements to display
 * @property {string} [facilityName] - Medical facility name
 * @property {string} [facilityAddress] - Medical facility address
 * @property {PersonInterface | null} [personInfo] - Patient person information
 * @property {Function} t - Translation function for i18n
 * @property {boolean} [isFiltered] - Whether data is filtered (affects legend display)
 * @property {Object} [filterInfo] - Filter criteria information
 * @property {string} filterInfo.type - Filter type (SPECIFIC_DATE, SPECIFIC_MONTH, etc.)
 * @property {Date} [filterInfo.date] - Specific date filter
 * @property {number} [filterInfo.selectedMonth] - Month filter (0-11)
 * @property {number} [filterInfo.selectedYear] - Year filter
 * @property {Date} [filterInfo.startDate] - Start date for range filter
 * @property {Date} [filterInfo.endDate] - End date for range filter
 */
interface PrintAccountingMovementsProps {
  movements: AccountingMovementInterface[];
  facilityName?: string;
  facilityAddress?: string;
  personInfo?: PersonInterface | null;
  t: (key: string) => string;
  isFiltered?: boolean;
  filterInfo?: {
    type: string;
    date?: Date;
    selectedMonth?: number;
    selectedYear?: number;
    startDate?: Date;
    endDate?: Date;
  };
}

/**
 * Generates printable accounting movements (patient account statement) template.
 * Creates formatted HTML document showing charges, payments, and account balance.
 * 
 * Features:
 * - Professional account statement format
 * - Chronologically sorted movements
 * - Patient demographic information
 * - Facility header information
 * - Filter legend showing date range/criteria
 * - Previous account state (before filtered period)
 * - Detailed transaction table (date, concept, charges, posted, amount due)
 * - Summary section with totals
 * - Print timestamp and generation info
 * - Print-friendly styling with page breaks
 * - Conditional amount due display logic
 * 
 * Movement Types:
 * - Billing Encounters: Shows as charges (positive amounts)
 * - Payments: Shows as posted amounts (reduces balance)
 * 
 * Calculations:
 * - Total Amount: Sum of all charges
 * - Total Posted: Sum of all payments
 * - Amount Due: Total Amount - Total Posted
 * - Previous State: Account state before filtered period begins
 * 
 * Previous State Logic:
 * - Only shown when data is filtered
 * - Calculates totals before first movement in filtered set
 * - Helps understand starting balance for period
 * 
 * Amount Due Display Logic:
 * - Shows at last row
 * - Shows when movement type changes (charge to payment or vice versa)
 * - Avoids redundant display in consecutive same-type movements
 * 
 * Filter Legend:
 * - Historical statement (no filter)
 * - Specific date
 * - Specific month and year
 * - Specific year
 * - Date range (start - end)
 * 
 * @param {PrintAccountingMovementsProps} props - Template properties
 * @returns {string} Complete HTML document ready for printing
 */
export const PrintAccountingMovements = ({
  movements,
  facilityName = "",
  facilityAddress = "",
  personInfo,
  t,
  isFiltered = false,
  filterInfo
}: PrintAccountingMovementsProps) => {
  const sortedMovements = [...movements].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA - dateB;
  });

  let patientName = "";
  let patientLastname = "";
  let patientFullName = "";
  let patientEmail = "";
  let patientDob = "";

  if (personInfo) {
    patientName = personInfo.name || "";
    patientLastname = personInfo.lastname || "";
    patientFullName = `${patientName} ${patientLastname}`.trim();
    patientEmail = personInfo.user?.email || "";
    patientDob = personInfo.dob ? formatDateForDisplay(personInfo.dob) : "";
  }

  const finalAmountDue = sortedMovements.length > 0 ? sortedMovements[sortedMovements.length - 1].amountDue : 0;
  const finalTotalAmount = sortedMovements.length > 0 ? sortedMovements[sortedMovements.length - 1].totalAmount : 0;

  /**
   * Generates filter legend text based on filter criteria.
   * Describes what data is being shown in the report.
   * 
   * @returns {string} Formatted filter legend text
   */
  const getFilterLegend = (): string => {
    if (!isFiltered || !filterInfo) {
      return t("accountingMovements.historicalAccountStatement");
    }

    const months = [
      t("months.january"), t("months.february"), t("months.march"), t("months.april"),
      t("months.may"), t("months.june"), t("months.july"), t("months.august"),
      t("months.september"), t("months.october"), t("months.november"), t("months.december")
    ];

    switch (filterInfo.type) {
      case DateFilterTypeEnum.SPECIFIC_DATE:
        return filterInfo.date 
          ? `${t("accountingMovements.filteredBy")} ${t("accountingMovements.specificDate")}: ${formatDateForDisplay(filterInfo.date)}`
          : t("accountingMovements.historicalAccountStatement");
      
      case DateFilterTypeEnum.SPECIFIC_MONTH:
        return filterInfo.selectedMonth !== undefined && filterInfo.selectedYear !== undefined
          ? `${t("accountingMovements.filteredBy")} ${t("accountingMovements.specificMonth")}: ${months[filterInfo.selectedMonth]} ${filterInfo.selectedYear}`
          : t("accountingMovements.historicalAccountStatement");
      
      case DateFilterTypeEnum.SPECIFIC_YEAR:
        return filterInfo.selectedYear !== undefined
          ? `${t("accountingMovements.filteredBy")} ${t("accountingMovements.specificYear")}: ${filterInfo.selectedYear}`
          : t("accountingMovements.historicalAccountStatement");
      
      case DateFilterTypeEnum.DATE_RANGE:
        return filterInfo.startDate && filterInfo.endDate
          ? 
            (new Date(filterInfo.startDate).getTime() === new Date(filterInfo.endDate).getTime())
            ? `${t("accountingMovements.filteredBy")} ${t("accountingMovements.specificDate")}: ${formatDateForDisplay(filterInfo.startDate)}`
            : `${t("accountingMovements.filteredBy")} ${t("accountingMovements.dateRange")}: ${formatDateForDisplay(filterInfo.startDate)} - ${formatDateForDisplay(filterInfo.endDate)}`
          : filterInfo.date 
            ? `${t("accountingMovements.filteredBy")} ${t("accountingMovements.specificDate")}: ${formatDateForDisplay(filterInfo.date)}`
            : t("accountingMovements.historicalAccountStatement");
      
      default:
        return t("accountingMovements.historicalAccountStatement");
    }
  };

  /**
   * Calculates account state before the first filtered movement.
   * Determines starting balance for the filtered period.
   * 
   * Logic:
   * - Takes first movement's totals
   * - Subtracts first movement's charge/payment to get previous state
   * - If first movement is charge: subtract charge amount
   * - If first movement is payment: add payment back
   * 
   * @returns {Object | null} Previous state with totalAmount, totalPosted, amountDue or null if not applicable
   */
  const calculatePreviousState = () => {
    if (!isFiltered || sortedMovements.length === 0) {
      return null;
    }

    const firstMovement = sortedMovements[0];
    
    if (!firstMovement.totalAmount || !firstMovement.amountDue) {
      return null;
    }

    let previousTotalAmount = firstMovement.totalAmount;
    let previousAmountDue = firstMovement.amountDue;
    let previousTotalPosted = firstMovement.totalAmount - firstMovement.amountDue;

    try {
      if (firstMovement.billingEncounter !== null && firstMovement.billingEncounter !== undefined) {
        const chargeAmount = firstMovement.billingEncounter.price || 0;
        if (typeof chargeAmount === 'number' && !isNaN(chargeAmount)) {
          previousTotalAmount -= chargeAmount;
          previousAmountDue -= chargeAmount;
        }
      } else if (firstMovement.payment !== null && firstMovement.payment !== undefined) {
        const postedAmount = firstMovement.payment.posted || 0;
        if (typeof postedAmount === 'number' && !isNaN(postedAmount)) {
          previousTotalPosted -= postedAmount;
          previousAmountDue += postedAmount;
        }
      }
      if (isNaN(previousTotalAmount) || isNaN(previousAmountDue) || isNaN(previousTotalPosted)) {
        return null;
      }

      return {
        totalAmount: previousTotalAmount,
        totalPosted: previousTotalPosted,
        amountDue: previousAmountDue,
      };
    } catch (error) {
      console.error("Error calculating previous state:", error);
      return null;
    }
  };

  const previousState = calculatePreviousState();

  /**
   * Gets movement concept description.
   * Returns "Payment" for payments, pricing name for charges.
   * 
   * @param {AccountingMovementInterface} movement - Movement record
   * @returns {string} Concept description
   */
  const getMovementConcept = (movement: AccountingMovementInterface) => {
    if (movement.payment !== null && movement.payment !== undefined) {
      return t("accountingMovements.payment");
    } else if (movement.billingEncounter !== null && movement.billingEncounter !== undefined) {
      return movement.billingEncounter.pricingName || t("accountingMovements.billingEncounter");
    }
    return "-";
  };

  /**
   * Gets charge amount from billing encounter.
   * 
   * @param {AccountingMovementInterface} movement - Movement record
   * @returns {string} Formatted charge amount or "-"
   */
  const getMovementCharge = (movement: AccountingMovementInterface) => {
    if (movement.billingEncounter !== null && movement.billingEncounter !== undefined) {
      return formatPrice(movement.billingEncounter.price || 0);
    }
    return "-";
  };

  /**
   * Gets posted amount from payment.
   * 
   * @param {AccountingMovementInterface} movement - Movement record
   * @returns {string} Formatted posted amount or "-"
   */
  const getMovementPosted = (movement: AccountingMovementInterface) => {
    if (movement.payment !== null && movement.payment !== undefined) {
      return formatPrice(movement.payment.posted || 0);
    }
    return "-";
  };

  /**
   * Checks if movement is a charge (billing encounter).
   * 
   * @param {AccountingMovementInterface} movement - Movement record
   * @returns {boolean} True if charge
   */
  const isCharge = (movement: AccountingMovementInterface) => {
    return movement.billingEncounter !== null && movement.billingEncounter !== undefined;
  };

  /**
   * Checks if movement is a payment.
   * 
   * @param {AccountingMovementInterface} movement - Movement record
   * @returns {boolean} True if payment
   */
  const isPayment = (movement: AccountingMovementInterface) => {
    return movement.payment !== null && movement.payment !== undefined;
  };

  /**
   * Determines if amount due should be displayed for this movement.
   * Shows at last row or when movement type changes.
   * 
   * @param {number} currentIndex - Index of current movement
   * @returns {boolean} True if amount due should be shown
   */
  const shouldShowAmountDue = (currentIndex: number): boolean => {
    if (currentIndex === sortedMovements.length - 1) {
      return true;
    }
    
    const currentMovement = sortedMovements[currentIndex];
    const nextMovement = sortedMovements[currentIndex + 1];
    return (isCharge(currentMovement) && isPayment(nextMovement)) || 
           (isPayment(currentMovement) && isCharge(nextMovement));
  };

  const movementRows = sortedMovements.map((movement, index) => {
    const concept = getMovementConcept(movement);
    const date = formatDateForDisplay(movement.date || movement.createdAt);
    const charge = getMovementCharge(movement);
    const posted = getMovementPosted(movement);
    const amountDue = shouldShowAmountDue(index) ? formatPrice(movement.amountDue) : "-";

    return `
      <tr>
        <td>${date}</td>
        <td>${concept}</td>
        <td style="text-align: right;">${charge}</td>
        <td style="text-align: right;">${posted}</td>
        <td style="text-align: right;">${amountDue}</td>
      </tr>
    `;
  }).join('');

  const style = `
    <style>
      body {
        font-family: Arial, sans-serif;
        margin: 20px;
        padding: 0;
        font-size: 12px;
        line-height: 1.4;
      }
      
      .print-container {
        max-width: 900px;
        margin: 0 auto;
      }
      
      .header {
        text-align: center;
        margin-bottom: 15px;
        border-bottom: 2px solid #333;
        padding-bottom: 10px;
      }
      
      .header h1 {
        margin: 0 0 10px 0;
        font-size: 20px;
        color: #333;
        font-weight: bold;
      }
      
      .filter-legend {
        font-size: 13px;
        color: #666;
        margin: 8px 0;
        font-style: italic;
        background-color: #f8f9fa;
        padding: 8px 12px;
        border-radius: 4px;
      }
      
      .facility-info {
        font-size: 11px;
        color: #666;
        margin: 5px 0;
      }
      
      .patient-info {
        background-color: #f8f9fa;
        padding: 15px;
        border-radius: 6px;
        margin: 15px 0;
        border: 1px solid #e9ecef;
      }
      
      .patient-info h3 {
        margin: 0 0 10px 0;
        font-size: 14px;
        color: #333;
        border-bottom: 1px solid #dee2e6;
        padding-bottom: 5px;
      }
      
      .patient-details {
        font-size: 12px;
        line-height: 1.5;
      }
      
      .patient-detail {
        display: inline;
        margin-right: 25px;
      }
      
      .patient-detail-label {
        font-weight: bold;
        color: #495057;
        margin-right: 5px;
      }
      
      .patient-detail-value {
        color: #333;
      }

      .previous-state-section {
        background-color: #fff3cd;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        padding: 15px;
        margin: 15px 0;
      }

      .previous-state-section h3 {
        margin: 0 0 10px 0;
        font-size: 14px;
        border-bottom: 1px solid;
        padding-bottom: 5px;
      }

      .previous-state-row {
        display: flex;
        justify-content: space-between;
        margin: 5px 0;
        padding: 3px 0;
        font-size: 12px;
      }

      .previous-state-label {
        font-weight: 500;
      }

      .previous-state-value {
        font-weight: 600;
      }
      
      .movements-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 15px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      }
      
      .movements-table th,
      .movements-table td {
        border: 1px solid #dee2e6;
        padding: 8px 6px;
        text-align: left;
      }
      
      .movements-table th:nth-child(3),
      .movements-table th:nth-child(4),
      .movements-table th:nth-child(5),
      .movements-table td:nth-child(3),
      .movements-table td:nth-child(4),
      .movements-table td:nth-child(5) {
        width: 100px;
        min-width: 100px;
        max-width: 100px;
      }
      
      .movements-table th {
        background-color: #f8f9fa;
        font-weight: bold;
        color: #495057;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .movements-table td {
        font-size: 11px;
        color: #333;
      }
      
      .movements-table tbody tr:nth-child(even) {
        background-color: #f8f9fa;
      }
      
      .movements-table tbody tr:hover {
        background-color: #e9ecef;
      }
      
      .summary-section {
        margin-top: 20px;
        padding: 15px;
        background-color: #f8f9fa;
        border-radius: 6px;
        border: 1px solid #e9ecef;
      }
      
      .summary-section h3 {
        margin: 0 0 15px 0;
        font-size: 14px;
        color: #333;
        border-bottom: 1px solid #dee2e6;
        padding-bottom: 8px;
      }
      
      .summary-row {
        display: flex;
        justify-content: space-between;
        margin: 8px 0;
        padding: 5px 0;
        font-size: 12px;
      }
      
      .summary-row.total {
        font-weight: bold;
        font-size: 13px;
        border-top: 1px solid #dee2e6;
        padding-top: 10px;
        margin-top: 10px;
      }
      
      .summary-label {
        color: #495057;
      }
      
      .summary-value {
        font-weight: 600;
        color: #333;
      }
      
      .print-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 20px;
        padding-top: 10px;
        border-top: 1px solid #dee2e6;
        font-size: 10px;
        color: #6c757d;
      }
      
      .no-data {
        text-align: center;
        padding: 40px;
        color: #6c757d;
        font-style: italic;
      }
      
      @media print {
        body {
          margin: 0;
          font-size: 11px;
        }
        
        .print-container {
          max-width: none;
        }
        
        .header {
          break-inside: avoid;
          page-break-after: avoid;
        }
        
        .filter-legend {
          break-inside: avoid;
          page-break-after: avoid;
        }
        
        .patient-info {
          break-inside: avoid;
          page-break-after: avoid;
        }

        .previous-state-section {
          break-inside: avoid;
          page-break-after: avoid;
        }
        
        .movements-table {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        
        .movements-table thead {
          break-inside: avoid;
          page-break-after: avoid;
        }
        
        .movements-table tbody tr {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        
        .summary-section {
          break-inside: avoid;
          page-break-inside: avoid;
        }
      }
    </style>
  `;
  
  const finalTotalPosted = sortedMovements.length > 0 ? finalTotalAmount - finalAmountDue : 0;

  return `
    <html>
      <head>
        <title>${t("accountingMovements.printTitle")}</title>
        ${style}
      </head>
      <body>
        <div class="print-container">
          <div class="header">
            <h1>${t("accountingMovements.reportTitle")}</h1>
            <div class="filter-legend">
              ${getFilterLegend()}
            </div>
            ${facilityName ? `<div class="facility-info">${facilityName}</div>` : ''}
            ${facilityAddress ? `<div class="facility-info">${facilityAddress}</div>` : ''}
          </div>
          
          ${patientFullName ? `
            <div class="patient-info">
              <h3>${t("accountingMovements.patientInformation")}</h3>
              <div class="patient-details">
                <div class="patient-detail">
                  <span class="patient-detail-label">${t("accountingMovements.patientName")}:</span>
                  <span class="patient-detail-value">${patientFullName}</span>
                </div>
                ${patientDob ? `
                  <div class="patient-detail">
                    <span class="patient-detail-label">${t("accountingMovements.birthDate")}:</span>
                    <span class="patient-detail-value">${patientDob}</span>
                  </div>
                ` : ''}
                ${patientEmail ? `
                  <div class="patient-detail">
                    <span class="patient-detail-label">${t("accountingMovements.email")}:</span>
                    <span class="patient-detail-value">${patientEmail}</span>
                  </div>
                ` : ''}
              </div>
            </div>
          ` : ''}

          ${previousState && typeof previousState.totalAmount === 'number' ? `
            <div class="previous-state-section">
              <h3>${t("accountingMovements.previousAccountState")}</h3>
              <div class="previous-state-row">
                <span class="previous-state-label">${t("accountingMovements.previousTotalAmount")}:</span>
                <span class="previous-state-value">${formatPrice(previousState.totalAmount || 0)}</span>
              </div>
              <div class="previous-state-row">
                <span class="previous-state-label">${t("accountingMovements.previousTotalPosted")}:</span>
                <span class="previous-state-value">${formatPrice(previousState.totalPosted || 0)}</span>
              </div>
              <div class="previous-state-row">
                <span class="previous-state-label">${t("accountingMovements.previousAmountDue")}:</span>
                <span class="previous-state-value">${formatPrice(previousState.amountDue || 0)}</span>
              </div>
            </div>
          ` : ''}
          
          ${sortedMovements.length === 0 ? `
            <div class="no-data">
              ${t("accountingMovements.noMovements")}
            </div>
          ` : `
            <table class="movements-table">
              <thead>
                <tr>
                  <th>${t("accountingMovements.date")}</th>
                  <th>${t("accountingMovements.concept")}</th>
                  <th>${t("accountingMovements.charges")}</th>
                  <th>${t("accountingMovements.posted")}</th>
                  <th>${t("accountingMovements.amountDue")}</th>
                </tr>
              </thead>
              <tbody>
                ${movementRows}
              </tbody>
            </table>

            <div class="summary-section">
              <h3>${t("accountingMovements.summary")}</h3>
              <div class="summary-row">
                <span class="summary-label">${t("accountingMovements.totalMovements")}:</span>
                <span class="summary-value">${sortedMovements.length}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">${t("accountingMovements.finalTotalAmount")}:</span>
                <span class="summary-value">${formatPrice(finalTotalAmount)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">${t("accountingMovements.finalTotalPosted")}:</span>
                <span class="summary-value">${formatPrice(finalTotalPosted)}</span>
              </div>
              <div class="summary-row total">
                <span class="summary-label">${t("accountingMovements.finalAmountDue")}:</span>
                <span class="summary-value">${formatPrice(finalAmountDue)}</span>
              </div>
            </div>
          `}

          <div class="print-info">
            <span>${t("accountingMovements.generatedBy")}: ${t("common.systemName")}</span>
            <span>${t("accountingMovements.printDate")}: ${formatDateForDisplay(new Date())} ${new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </body>
    </html>
  `;
};

export default PrintAccountingMovements;